import { Facebook, Twitter, Instagram, Linkedin, Youtube, Music2, MessageCircle, Phone, Mail, MapPin } from 'lucide-react';
import { brandSettings, contactSettings, socialMediaSettings } from '../config/brand-config';
import darkLogo from 'figma:asset/d1fe1a9231ec0614e594fed0b7cebc9d8b920db6.png';

interface FooterProps {
  onNavigate: (page: any) => void;
}

export function Footer({ onNavigate }: FooterProps) {
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { 
      icon: Facebook, 
      url: socialMediaSettings.facebook, 
      label: 'Facebook',
      color: 'hover:text-blue-600'
    },
    { 
      icon: Twitter, 
      url: socialMediaSettings.twitter, 
      label: 'Twitter',
      color: 'hover:text-blue-400'
    },
    { 
      icon: Instagram, 
      url: socialMediaSettings.instagram, 
      label: 'Instagram',
      color: 'hover:text-pink-600'
    },
    { 
      icon: Linkedin, 
      url: socialMediaSettings.linkedin, 
      label: 'LinkedIn',
      color: 'hover:text-blue-700'
    },
    {
      icon: Youtube,
      url: socialMediaSettings.youtube,
      label: 'YouTube',
      color: 'hover:text-red-600'
    },
    {
      icon: Music2,
      url: socialMediaSettings.tiktok,
      label: 'TikTok',
      color: 'hover:text-pink-500'
    },
  ].filter(link => link.url); // Only show links that are configured

  const logoSrc = brandSettings.logo || darkLogo;

  return (
    <footer className="border-t border-border/50 bg-card/30 backdrop-blur-xl pb-20 md:pb-0">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="col-span-1 md:col-span-2">
            <div className="mb-6">
              <img 
                src={logoSrc}
                alt={`${brandSettings.companyName} Logo`}
                className="h-12 w-auto mb-4"
              />
            </div>
            <p className="text-sm text-muted-foreground">
              Your trusted partner in finding the perfect property. We're dedicated to making your property dreams a reality.
            </p>
          </div>

          {/* Contact Information */}
          <div>
            <h4 className="mb-4 font-semibold">Contact Us</h4>
            <div className="space-y-3">
              <a 
                href={`tel:${contactSettings.phone}`}
                className="flex items-center text-sm text-muted-foreground hover:text-primary transition-colors group"
              >
                <Phone className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform" />
                {contactSettings.phone}
              </a>
              <a 
                href={`mailto:${contactSettings.email}`}
                className="flex items-center text-sm text-muted-foreground hover:text-primary transition-colors group"
              >
                <Mail className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform" />
                {contactSettings.email}
              </a>
              <div className="flex items-start text-sm text-muted-foreground">
                <MapPin className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                <div>
                  <p>{contactSettings.address}</p>
                  <p>{contactSettings.city}, {contactSettings.postalCode}</p>
                  <p>{contactSettings.country}</p>
                </div>
              </div>
              {socialMediaSettings.whatsapp && (
                <a 
                  href={`https://wa.me/${socialMediaSettings.whatsapp.replace(/[^0-9]/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center text-sm text-muted-foreground hover:text-green-500 transition-colors group"
                >
                  <MessageCircle className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform" />
                  WhatsApp: {socialMediaSettings.whatsapp}
                </a>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="mb-4 font-semibold">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <button 
                  onClick={() => onNavigate('properties')} 
                  className="text-sm text-muted-foreground hover:text-primary transition-colors text-left bg-transparent border-0 p-0 cursor-pointer"
                >
                  Browse Properties
                </button>
              </li>
              <li>
                <button 
                  onClick={() => onNavigate('services')} 
                  className="text-sm text-muted-foreground hover:text-primary transition-colors text-left bg-transparent border-0 p-0 cursor-pointer"
                >
                  Our Services
                </button>
              </li>
              <li>
                <button 
                  onClick={() => onNavigate('pre-approval')} 
                  className="text-sm text-muted-foreground hover:text-primary transition-colors text-left bg-transparent border-0 p-0 cursor-pointer"
                >
                  Bond Pre-Approval
                </button>
              </li>
              <li>
                <button 
                  onClick={() => onNavigate('agents')} 
                  className="text-sm text-muted-foreground hover:text-primary transition-colors text-left bg-transparent border-0 p-0 cursor-pointer"
                >
                  Meet Our Agents
                </button>
              </li>
              <li>
                <button 
                  onClick={() => onNavigate('about')} 
                  className="text-sm text-muted-foreground hover:text-primary transition-colors text-left bg-transparent border-0 p-0 cursor-pointer"
                >
                  About Us
                </button>
              </li>
              <li>
                <button 
                  onClick={() => onNavigate('blog')} 
                  className="text-sm text-muted-foreground hover:text-primary transition-colors text-left bg-transparent border-0 p-0 cursor-pointer"
                >
                  Blog
                </button>
              </li>
              <li>
                <button 
                  onClick={() => onNavigate('contact')} 
                  className="text-sm text-muted-foreground hover:text-primary transition-colors text-left bg-transparent border-0 p-0 cursor-pointer"
                >
                  Contact Us
                </button>
              </li>
              <li>
                <button 
                  onClick={() => onNavigate('seller')} 
                  className="text-sm text-muted-foreground hover:text-primary transition-colors text-left bg-transparent border-0 p-0 cursor-pointer"
                >
                  Sell Your Property
                </button>
              </li>
              <li>
                <button 
                  onClick={() => onNavigate('agent')} 
                  className="text-sm text-muted-foreground hover:text-primary transition-colors text-left bg-transparent border-0 p-0 cursor-pointer"
                >
                  Agent Portal
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Social Media & Copyright */}
        <div className="border-t border-border/50 mt-8 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Social Media Links */}
            {socialLinks.length > 0 && (
              <div className="flex items-center gap-4">
                <span className="text-sm text-muted-foreground">Follow us:</span>
                <div className="flex items-center gap-3">
                  {socialLinks.map((social) => {
                    const Icon = social.icon;
                    return (
                      <a
                        key={social.label}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`text-muted-foreground transition-all hover:scale-110 ${social.color}`}
                        aria-label={social.label}
                      >
                        <Icon className="w-5 h-5" />
                      </a>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Copyright */}
            <p className="text-sm text-muted-foreground">
              © {currentYear} {brandSettings.companyName}. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}